import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import type { HuayBetItem } from './ModernLobbyHuayBetSideLeft'
import type { BettingPackage } from './ModernLobbyHuayLotteryTermsPage'
import type { LotteryRound } from '../../services/lotteryService'
import { lotteryService } from '../../services/lotteryService'
import * as walletService from '../../services/walletService'
import { getRuntimeTenantIdSync } from '../../lib/runtimeTenant'
import { resolvePrizeTypeLabel } from '../../utils/prizeTypeLabels'
import { buildPayoutRatesTableData } from '../../utils/payoutRatesTable'
import { hasCustomPayoutRateForRoundLimit, type RoundLimitsLike } from '../../utils/roundLimitNumberUtils'

function toast(msg: string, opts?: { icon?: 'info' | 'warning' | 'success' | 'error' }) {
  Swal.fire({
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    icon: opts?.icon ?? 'info',
    title: msg,
  })
}

const PRICE_OPTIONS_SAME = [5, 10, 20, 50, 100, 500]

export interface ModernLobbyHuayBetSideRightProps {
  countdown: string
  betItems: HuayBetItem[]
  removeBetItem?: (id: string) => void
  updateBetAmount?: (id: string, amount: number) => void
  clearAllBetItems?: () => void
  submitBetItems?: () => void | Promise<void>
  isSubmittingBet?: boolean
  defaultAmount: number
  setDefaultAmount: (amount: number) => void
  creditBalance?: number
  isLoadingWallet?: boolean
  /** แพ็คเกจที่เลือก — ใช้ใน validate-bet API และตารางราคาจ่าย */
  selectedPackage?: BettingPackage
  /** รอบจาก API — ใช้ตรวจ isYeekee และโหลด/ส่งเลขยี่กี่ */
  round?: LotteryRound
  /** จำนวนแพ็กเกจ (ถ้ามีแค่ 1 จะ disable ปุ่มเปลี่ยนแปลงราคาแทง) */
  packageCount?: number
}

interface BetLimitInfo {
  limitType: string
  cap: number
  currentTotal: number
  requestedAmount: number
  nextTotal: number
  remainingAllowed: number
  remainingAfterItem: number
  exceededBy: number
  suggestedAmount: number
  minimumBet?: number
}

interface ValidatedBetItemState {
  isValid: boolean
  isBlocked?: boolean
  reason?: string
  payoutRate?: number
  discount?: number
  potentialPayout?: number
  amount?: number
  limitInfo?: BetLimitInfo | null
  betLimits?: {
    minBet: number
    maxBet: number
  } | null
}

/** ฝั่งขวา — ลำดับและ class ตรง huaybet.html 1961-3058 ทุกบล็อก */
const ModernLobbyHuayBetSideRight: React.FC<ModernLobbyHuayBetSideRightProps> = ({
  countdown,
  betItems,
  removeBetItem,
  updateBetAmount,
  clearAllBetItems,
  submitBetItems,
  isSubmittingBet = false,
  defaultAmount: _defaultAmount,
  setDefaultAmount: _setDefaultAmount,
  creditBalance,
  isLoadingWallet = false,
  selectedPackage,
  round,
  packageCount,
}) => {
  const navigate = useNavigate()
  const walletBalance = creditBalance ?? 0
  const creditDisplayText = isLoadingWallet ? '...' : walletService.formatCurrency(walletBalance)

  const rowStyle = { rowGap: '0.5rem' as const }

  const isYeekee = useMemo(() => {
    if (!round) return false
    const huayTypeName = round.huayTypeName || ''
    const huayName = round.huayName || ''
    return huayTypeName.includes('ยี่กี') || huayTypeName.includes('ยี') || huayName.includes('ยี่กี') || huayName.includes('ยี')
  }, [round])

  const yeekeeNumberDigits = useMemo(() => {
    const configured = Number(round?.yeekeeConfig?.numberDigits)
    return Number.isInteger(configured) && configured >= 5 && configured <= 8 ? configured : 5
  }, [round?.yeekeeConfig?.numberDigits])

  type YeekeeShot = { _id?: string; number: string; displayPhoneNumber?: string; username?: string; shotAt: string; shooterType?: string }
  const [yeekeeShots, setYeekeeShots] = useState<YeekeeShot[]>([])
  const [loadingShots, setLoadingShots] = useState(false)
  const [predictionNumber, setPredictionNumber] = useState('')
  const hasLoadedShotsOnce = useRef(false)
  const yeekeeInputRefs = useRef<(HTMLInputElement | null)[]>([])
  /** Countdown จริงสำหรับบล็อกยิงเลขยี่กี่ — อัปเดตทุกวินาทีจาก round.betEndTime */
  const [yeekeeCountdown, setYeekeeCountdown] = useState(countdown)

  const tenantId = getRuntimeTenantIdSync()
  const baseUrl = (import.meta.env as { VITE_API_BASE_URL?: string }).VITE_API_BASE_URL || 'http://localhost:8081'

  useEffect(() => {
    if (!round?.betEndTime) {
      setYeekeeCountdown(countdown)
      return
    }
    const update = () => setYeekeeCountdown(lotteryService.formatTimeRemaining(round.betEndTime))
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [round?.betEndTime, countdown])

  useEffect(() => {
    if (!isYeekee || !round?._id) return
    const roundId = round._id
    const loadShots = async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      const isFirst = !hasLoadedShotsOnce.current
      try {
        if (isFirst) setLoadingShots(true)
        const token = localStorage.getItem('accessToken')
        const res = await fetch(`${baseUrl}/api/v1/lottery/tenant/${tenantId}/yeekee/rounds/${roundId}/shots`, {
          headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        })
        const data = await res.json()
        if (data?.success && Array.isArray(data.shots)) setYeekeeShots(data.shots)
      } catch (e) {
        console.error('Yeekee shots load error', e)
      } finally {
        if (isFirst) {
          hasLoadedShotsOnce.current = true
          setLoadingShots(false)
        }
      }
    }
    loadShots()
    const id = setInterval(loadShots, 15_000)
    return () => clearInterval(id)
  }, [isYeekee, round?._id, baseUrl, tenantId])

  useEffect(() => {
    if (!isYeekee) return
    setPredictionNumber((prev) => prev.slice(0, yeekeeNumberDigits))
  }, [isYeekee, yeekeeNumberDigits])

  const handleRandomPrediction = useCallback(() => {
    setPredictionNumber(
      Array.from({ length: yeekeeNumberDigits }, () => String(Math.floor(Math.random() * 10))).join('')
    )
  }, [yeekeeNumberDigits])

  const handleSubmitPrediction = useCallback(async () => {
    if (!round?._id) return
    if (!predictionNumber || predictionNumber.length !== yeekeeNumberDigits) {
      toast(`กรุณาใส่เลขให้ครบ ${yeekeeNumberDigits} หลัก`, { icon: 'warning' })
      return
    }
    const token = localStorage.getItem('accessToken')
    const userStr = localStorage.getItem('user')
    const user = userStr ? JSON.parse(userStr) : null
    const userId = user?.id ?? user?.userId
    const username = user?.username ?? user?.name
    const displayPhoneNumber = user?.mobile ?? user?.phone
    try {
      const res = await fetch(`${baseUrl}/api/v1/lottery/tenant/${tenantId}/yeekee/shoot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          roundId: round._id,
          number: predictionNumber,
          userId,
          ...(username ? { username } : {}),
          ...(displayPhoneNumber ? { displayPhoneNumber } : {}),
        }),
      })
      const data = await res.json()
      if (data?.success) {
        setPredictionNumber('')
        const shotsRes = await fetch(`${baseUrl}/api/v1/lottery/tenant/${tenantId}/yeekee/rounds/${round._id}/shots`, {
          headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        })
        const shotsData = await shotsRes.json()
        if (shotsData?.success && Array.isArray(shotsData.shots)) setYeekeeShots(shotsData.shots)
        toast('ส่งเลขออกรางวัลเรียบร้อย', { icon: 'success' })
      } else {
        toast(data?.message || data?.error || 'เกิดข้อผิดพลาด', { icon: 'error' })
      }
    } catch (e) {
      console.error('Submit prediction error', e)
      toast('เกิดข้อผิดพลาดในการส่งเลข', { icon: 'error' })
    }
  }, [round?._id, predictionNumber, yeekeeNumberDigits, baseUrl, tenantId])

  // Helpers สำหรับยืนยันส่งโพย (ตรงกับ user-classic LotteryBetSideRight)
  const formatRoundDisplay = useCallback((r: LotteryRound | undefined): string => {
    if (!r) return 'ไม่ระบุ'
    if (r.roundNumber) {
      if (r.drawDate) {
        try {
          const d = new Date(r.drawDate)
          const fmt = d.toLocaleDateString('th-TH', { year: 'numeric', month: '2-digit', day: '2-digit' })
          return `รอบที่ ${r.roundNumber} (${fmt})`
        } catch {
          return `รอบที่ ${r.roundNumber}`
        }
      }
      return `รอบที่ ${r.roundNumber}`
    }
    if (r.drawDate) {
      try {
        const d = new Date(r.drawDate)
        const fmt = d.toLocaleDateString('th-TH', { year: 'numeric', month: '2-digit', day: '2-digit' })
        return `รอบ ${fmt}${r.drawTime ? ` ${r.drawTime}` : ''}`
      } catch {
        return `รอบ ${r._id.substring(0, 8)}...`
      }
    }
    return `รอบ ${r._id.substring(0, 8)}...`
  }, [])

  const formatBlockedReason = useCallback((reason: string | undefined): string => {
    if (!reason) return 'อั้นจ่าย'
    const r = reason.trim()
    const lower = r.toLowerCase()
    const formatPrizeTypeLabel = (code: string | undefined): string => {
      const key = String(code || '').trim().toUpperCase()
      const labels: Record<string, string> = {
        '3_DIGIT_TOP': '3 ตัวบน',
        '3_DIGIT_BOTTOM': '3 ตัวล่าง',
        '3_DIGIT_TOD': '3 ตัวโต๊ด',
        '2_DIGIT_TOP': '2 ตัวบน',
        '2_DIGIT_BOTTOM': '2 ตัวล่าง',
        '2_DIGIT_TOD': '2 ตัวโต๊ด',
        'RUNNING_TOP': 'วิ่งบน',
        'RUNNING_BOTTOM': 'วิ่งล่าง',
        '4_DIGIT_TOP': '4 ตัวบน',
        '4_DIGIT_TOD': '4 ตัวโต๊ด',
        '5_DIGIT_TOD': '5 ตัวโต๊ด',
        'FIXED_POSITION_HUNDREDS': 'ปักหลักร้อย',
        'FIXED_POSITION_TENS': 'ปักหลักสิบ',
        'FIXED_POSITION_ONES': 'ปักหลักหน่วย',
      }
      return labels[key] || code || '-'
    }
    if (lower.includes('some bet items are no longer valid')) {
      return 'มีบางรายการไม่สามารถซื้อได้แล้ว กรุณาลบรายการที่มีปัญหาออกแล้วลองใหม่'
    }
    if (
      lower.includes('account_betting_blocked') ||
      lower.includes('blocked from betting') ||
      lower.includes('กลุ่มจำกัดการแทง') ||
      lower.includes('บัญชีนี้ถูกบล็อกการแทง') ||
      lower.includes('บัญชีนี้ยังไม่สามารถทำรายการแทงได้')
    ) {
      return 'บัญชีนี้ยังไม่สามารถทำรายการแทงได้ กรุณาติดต่อเจ้าหน้าที่'
    }
    if (r === 'Number has reached its betting limit') return 'เลขถึงวงเงินแทงแล้ว (ไม่รับแทง)'
    if (lower.includes('would exceed total betting limit')) return 'เกินวงเงินแทงรวมของประเภทรางวัลนี้'
    if (lower.includes('prize type is temporarily paused')) return 'ประเภทรางวัลนี้หยุดรับชั่วคราว'
    if (lower.includes('prize type not found')) return 'ไม่พบประเภทรางวัลในรอบนี้'
    if (lower.includes('not active')) return 'ประเภทรางวัลนี้ไม่เปิดรับ'
    if (lower.includes('betting package not found') || lower.includes('package not found')) return 'ไม่พบแพ็กเกจการเดิมพัน กรุณาเลือกแพ็กเกจใหม่'
    if (lower.includes('bet amount must be between') || lower.includes('จำนวนเงินเดิมพันสำหรับ')) {
      return 'ยอดเดิมพันของรายการนี้เกินเงื่อนไขที่กำหนด กรุณาปรับยอดแล้วลองอีกครั้ง'
    }
    if (lower.includes('prize type') && lower.includes('not available')) {
      const match = r.match(/Prize type\s+([A-Z0-9_]+)\s+not available/i)
      if (match) return `ไม่พบประเภทรางวัล ${formatPrizeTypeLabel(match[1])}`
      return 'ไม่พบประเภทรางวัลในรอบนี้'
    }
    if (lower.includes('not available in package')) {
      const match = r.match(/Prize type\s+([A-Z0-9_]+)\s+not available in package\s+([A-Z0-9_]+)/i)
      if (match) return `ประเภทรางวัล ${formatPrizeTypeLabel(match[1])} ไม่รองรับในแพ็กเกจ ${match[2]}`
      return 'ประเภทรางวัลนี้ไม่รองรับในแพ็กเกจที่เลือก'
    }
    if (lower.includes('is currently disabled')) {
      const match = r.match(/Prize type\s+([A-Z0-9_]+)\s+is currently disabled/i)
      if (match) return `ประเภทรางวัล ${formatPrizeTypeLabel(match[1])} ปิดรับแทงอยู่ในขณะนี้`
      return 'ประเภทรางวัลนี้ปิดรับแทงอยู่ในขณะนี้'
    }
    if (lower.includes('invalid number length')) {
      const match = r.match(/Invalid number length for\s+([A-Z0-9_]+)\. Expected\s+(\d+)\s+digits/i)
      if (match) return `${formatPrizeTypeLabel(match[1])} ต้องมี ${match[2]} หลัก`
      return 'จำนวนหลักของเลขที่กรอกไม่ถูกต้อง'
    }
    if (lower.includes('number is blocked or full')) return 'เลขนี้ถูกปิดรับหรือเต็มแล้ว'
    if (lower.includes('missing prizetypecode')) return 'ไม่พบประเภทรางวัลของรายการแทง'
    return r
  }, [])

  const prizeTypeCodeForApi = useCallback((code: string): string => {
    if (code === '3_DIGIT_REVERSE') return '3_DIGIT_TOP'
    if (code === '2_DIGIT_REVERSE') return '2_DIGIT_TOP'
    return code
  }, [])

  const betTypeToPrizeTypeCode = useCallback((betType: string, betTypeLabel?: string): string => {
    if (betTypeLabel === 'ปักหลักร้อย') return 'FIXED_POSITION_HUNDREDS'
    if (betTypeLabel === 'ปักหลักสิบ') return 'FIXED_POSITION_TENS'
    if (betTypeLabel === 'ปักหลักหน่วย') return 'FIXED_POSITION_ONES'
    const mapping: Record<string, string> = {
      top3: '3_DIGIT_TOP', down3: '3_DIGIT_BOTTOM', tod3: '3_DIGIT_TOD', tod3reverse: '3_DIGIT_REVERSE',
      top2: '2_DIGIT_TOP', down2: '2_DIGIT_BOTTOM', tod2: '2_DIGIT_TOD', tod2reverse: '2_DIGIT_REVERSE',
      top1: 'RUNNING_TOP', down1: 'RUNNING_BOTTOM',
      top4: '4_DIGIT_TOP', tod4: '4_DIGIT_TOD', tod5: '5_DIGIT_TOD',
      puk1: 'FIXED_POSITION_ONES', puk10: 'FIXED_POSITION_TENS', puk100: 'FIXED_POSITION_HUNDREDS',
    }
    return mapping[betType] ?? betType.toUpperCase()
  }, [])

  const formatAmountDisplay = useCallback((value: number | undefined, forceDecimals = false): string => {
    const numericValue = Number(value || 0)
    return numericValue.toLocaleString('th-TH', {
      minimumFractionDigits: forceDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    })
  }, [])

  const getInlineValidationNotice = useCallback((validatedItem?: ValidatedBetItemState | null) => {
    if (!validatedItem || validatedItem.isValid) {
      return null
    }

    const translatedReason = formatBlockedReason(validatedItem.reason)
    const limitInfo = validatedItem.limitInfo || null

    if (
      String(validatedItem.reason || '').toLowerCase().includes('bet amount must be between')
      || String(validatedItem.reason || '').includes('จำนวนเงินเดิมพันสำหรับ')
    ) {
      const maxBet = Number(validatedItem.betLimits?.maxBet || 0)
      const minBet = Number(validatedItem.betLimits?.minBet || 0)
      const currentAmount = Number(validatedItem.amount || 0)
      if (maxBet > 0 && currentAmount > maxBet) {
        return {
          title: translatedReason,
          detail: `ยอดสูงสุดที่ซื้อได้สำหรับรายการนี้คือ ${formatAmountDisplay(maxBet)} บาท`,
          suggestedAmount: maxBet,
        }
      }
      if (minBet > 0 && currentAmount > 0 && currentAmount < minBet) {
        return {
          title: translatedReason,
          detail: `ยอดขั้นต่ำที่ซื้อได้สำหรับรายการนี้คือ ${formatAmountDisplay(minBet)} บาท`,
          suggestedAmount: minBet,
        }
      }
      return {
        title: translatedReason,
        detail: 'รายการนี้ยังไม่ผ่านเงื่อนไขของระบบ กรุณาปรับยอดก่อนส่งโพย',
        suggestedAmount: 0,
      }
    }

    if (!limitInfo) {
      return {
        title: translatedReason,
        detail: '',
        suggestedAmount: 0,
      }
    }

    if (limitInfo.remainingAllowed > 0) {
      const canAutoAdjust = Number(limitInfo.suggestedAmount || 0) >= Number(limitInfo.minimumBet || 1)
      return {
        title: translatedReason,
        detail: canAutoAdjust
          ? `รายการนี้ซื้อได้สูงสุดอีก ${formatAmountDisplay(limitInfo.remainingAllowed)} บาท`
          : 'รายการนี้ยังไม่ผ่านเงื่อนไขของระบบ กรุณาปรับยอดก่อนส่งโพย',
        suggestedAmount: canAutoAdjust ? Number(limitInfo.suggestedAmount || 0) : 0,
      }
    }

    return {
      title: translatedReason,
      detail: 'รายการนี้ไม่สามารถซื้อเพิ่มได้ในขณะนี้',
      suggestedAmount: 0,
    }
  }, [formatAmountDisplay, formatBlockedReason])

  // สถานะไฮไลท์เลขซ้ำและ validation รายบรรทัด
  const [duplicateHighlight, setDuplicateHighlight] = useState<{
    enabled: boolean
    sameAmountItems: string[]
    differentAmountItems: string[]
  }>({ enabled: false, sameAmountItems: [], differentAmountItems: [] })
  const [inlineValidationByItemId, setInlineValidationByItemId] = useState<Record<string, ValidatedBetItemState>>({})
  const [isInlineValidating, setIsInlineValidating] = useState(false)
  const inlineValidationRequestRef = useRef(0)

  const hasFreshInlineValidation = useCallback((item: HuayBetItem, validatedItem?: ValidatedBetItemState | null) => {
    if (!validatedItem) return false
    const validatedAmount = Number(validatedItem.amount)
    return Number.isFinite(validatedAmount) && validatedAmount === Number(item.amount)
  }, [])

  const isHardBlockedValidation = useCallback((validatedItem?: ValidatedBetItemState | null) => {
    if (!validatedItem?.isBlocked) return false
    const rawReason = String(validatedItem.reason || '').toLowerCase()
    return rawReason.includes('number is blocked or full')
      || rawReason.includes('number has reached its betting limit')
      || rawReason.includes('เลขนี้ถูกปิดรับหรือเต็มแล้ว')
      || rawReason.includes('เลขถึงวงเงินแทงแล้ว')
  }, [])

  const totalBet = useMemo(() => {
    return betItems.reduce((sum, item) => {
      const validatedItem = inlineValidationByItemId[item.id]
      const fresh = hasFreshInlineValidation(item, validatedItem)
      if (!fresh) return sum + Number(item.amount ?? 0)
      if (!validatedItem?.isValid) return sum
      return sum + Number(validatedItem.amount ?? item.amount ?? 0)
    }, 0)
  }, [betItems, inlineValidationByItemId, hasFreshInlineValidation])

  const buildOptimisticValidationState = useCallback(() => {
    return betItems.reduce<Record<string, ValidatedBetItemState>>((acc, item) => {
      const prizeTypeCode = prizeTypeCodeForApi(betTypeToPrizeTypeCode(item.betType, item.betTypeLabel))
      const prizeConfig = selectedPackage?.prizeConfigs?.find(
        (config) => String(config.prizeTypeCode || '').toUpperCase() === prizeTypeCode
      )
      const payoutRate = Number(prizeConfig?.payoutRate ?? item.multiplier ?? 0)
      const discount = Number(prizeConfig?.discount ?? item.discount ?? 0)
      const minBet = Number(prizeConfig?.minBet ?? 1)
      const maxBet = Number(prizeConfig?.maxBet ?? 100000)
      const amount = Number(item.amount || 0)
      const amountOutOfRange = amount > 0 && (amount < minBet || amount > maxBet)

      acc[item.id] = {
        isValid: !amountOutOfRange,
        reason: amountOutOfRange
          ? `จำนวนเงินเดิมพันสำหรับ ${resolvePrizeTypeLabel(prizeTypeCode)} ต้องอยู่ระหว่าง ${minBet} ถึง ${maxBet} บาท`
          : undefined,
        payoutRate,
        discount,
        potentialPayout: amount * payoutRate,
        amount,
        betLimits: { minBet, maxBet },
      }
      return acc
    }, {})
  }, [betItems, betTypeToPrizeTypeCode, prizeTypeCodeForApi, selectedPackage?.prizeConfigs])

  useEffect(() => {
    if (!round?._id || betItems.length === 0) {
      inlineValidationRequestRef.current += 1
      setInlineValidationByItemId({})
      setIsInlineValidating(false)
      return
    }

    const requestId = inlineValidationRequestRef.current + 1
    inlineValidationRequestRef.current = requestId
    setInlineValidationByItemId(buildOptimisticValidationState())
    setIsInlineValidating(true)

    const abortController = new AbortController()

    const timeoutId = window.setTimeout(async () => {
      try {
        const accessToken = localStorage.getItem('accessToken')
        const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId }
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

        const response = await axios.post(
          `${baseUrl}/api/v1/lottery/tenant/${tenantId}/validate-bet`,
          {
            roundId: round._id,
            packageCode: selectedPackage?.code,
            betItems: betItems.map((item) => {
              const code = betTypeToPrizeTypeCode(item.betType, item.betTypeLabel)
              return { prizeTypeCode: prizeTypeCodeForApi(code), number: item.number, amount: item.amount }
            }),
          },
          { headers, signal: abortController.signal }
        )

        if (inlineValidationRequestRef.current !== requestId) return

        const validatedItems = Array.isArray(response.data?.data?.validatedItems) ? response.data.data.validatedItems : []
        const nextValidationState = betItems.reduce<Record<string, ValidatedBetItemState>>((acc, item, index) => {
          acc[item.id] = validatedItems[index] || { isValid: true }
          return acc
        }, {})

        setInlineValidationByItemId(nextValidationState)
      } catch (error: any) {
        if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
          return
        }
        if (inlineValidationRequestRef.current === requestId) {
          console.error('Inline validate-bet failed:', error)
          setInlineValidationByItemId({})
        }
      } finally {
        if (inlineValidationRequestRef.current === requestId) {
          setIsInlineValidating(false)
        }
      }
    }, 800)

    return () => {
      window.clearTimeout(timeoutId)
      abortController.abort()
    }
  }, [baseUrl, betItems, betTypeToPrizeTypeCode, buildOptimisticValidationState, prizeTypeCodeForApi, round?._id, selectedPackage?.code, tenantId])

  const handleSubmitBetConfirmation = useCallback(async () => {
    if (isSubmittingBet) return
    if (!submitBetItems || betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ส่ง', { icon: 'warning' })
      return
    }
    if (!round?._id) {
      toast('ไม่พบข้อมูลรอบหวย', { icon: 'warning' })
      return
    }
    if (totalBet > walletBalance) {
      await Swal.fire({
        icon: 'warning',
        title: 'ยอดเงินไม่เพียงพอ',
        html: `ยอดเครดิตคงเหลือ <strong>${walletService.formatCurrency(walletBalance)}</strong> บาท น้อยกว่ายอดที่ต้องการแทง <strong>${totalBet.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong> บาท<br><br>กรุณาเติมเงินหรือลดจำนวนเดิมพัน`,
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#fd7e14',
      })
      return
    }
    Swal.fire({
      title: 'กำลังตรวจสอบ...',
      text: 'กรุณารอสักครู่',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => { Swal.showLoading() },
    })
    try {
      const accessToken = localStorage.getItem('accessToken')
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId }
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

      const betItemsForValidation = betItems.map((item) => {
        const code = betTypeToPrizeTypeCode(item.betType, item.betTypeLabel)
        return { prizeTypeCode: prizeTypeCodeForApi(code), number: item.number, amount: item.amount }
      })

      const response = await axios.post(
        `${baseUrl}/api/v1/lottery/tenant/${tenantId}/validate-bet`,
        { roundId: round._id, packageCode: selectedPackage?.code, betItems: betItemsForValidation },
        { headers }
      )
      Swal.close()

      if (!response.data.success) {
        toast('เกิดข้อผิดพลาดในการตรวจสอบ', { icon: 'warning' })
        return
      }

      const { validatedItems, summary } = response.data.data

      const lotteryCode = round?.huayName || round?.huayTypeName || 'ไม่ระบุ'
      const roundDisplay = formatRoundDisplay(round)

      const hasCustomPayoutRate = (prizeTypeCode: string, number: string): boolean =>
        hasCustomPayoutRateForRoundLimit(
          (round as { roundLimits?: RoundLimitsLike })?.roundLimits,
          prizeTypeCode,
          number,
        )

      let totalBetAmountCalc = 0
      let totalDiscount = 0
      validatedItems.forEach((item: { isBlocked?: boolean; isValid?: boolean; amount?: number; discount?: number }, index: number) => {
        if (item.isBlocked || !item.isValid) return
        const amt = item.amount ?? betItems[index]?.amount ?? 0
        totalBetAmountCalc += amt
        totalDiscount += amt * ((item.discount || 0) / 100)
      })

      const hasAnyAdjustedRate = validatedItems.some((validatedItem: { isBlocked?: boolean; isValid?: boolean; number?: string }, index: number) => {
        if (validatedItem.isBlocked || !validatedItem.isValid) return false
        const originalItem = betItems[index]
        const number = validatedItem.number || originalItem?.number || ''
        const prizeTypeCode = originalItem ? betTypeToPrizeTypeCode(originalItem.betType, originalItem.betTypeLabel) : ''
        return hasCustomPayoutRate(prizeTypeCode, number)
      })

      const betItemsTable = `
        <div style="max-height: 400px; overflow-y: auto; margin: 10px 0;">
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">ลำดับ</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">เลข</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">ชนิด</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">เงินแทง (฿)</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">จ่าย (฿)</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">ลด (%)</th>
                <th style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              ${validatedItems.map((validatedItem: { isBlocked?: boolean; isValid?: boolean; potentialPayout?: number; number?: string; amount?: number; discount?: number; reason?: string; limitInfo?: BetLimitInfo | null }, index: number) => {
                const originalItem = betItems[index]
                const isBlocked = validatedItem.isBlocked || !validatedItem.isValid
                const payout = validatedItem.potentialPayout || 0
                const number = validatedItem.number || originalItem?.number || ''
                const prizeTypeCode = originalItem ? betTypeToPrizeTypeCode(originalItem.betType, originalItem.betTypeLabel) : ''
                const isAdjustedRate = !isBlocked && hasCustomPayoutRate(prizeTypeCode, number)
                const rowColor = isBlocked ? '#ffebee' : (isAdjustedRate ? '#fff8e1' : '')
                const remainingHint = isBlocked && validatedItem.limitInfo?.remainingAllowed
                  ? `<br/><small style="display:block; margin-top:4px; color:#6c757d;">ซื้อได้อีก ${validatedItem.limitInfo.remainingAllowed.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} บาท</small>`
                  : ''
                const statusText = isBlocked
                  ? `<span style="color: #e74c3c; font-weight: bold;">${formatBlockedReason(validatedItem.reason)}</span>${remainingHint}`
                  : (isAdjustedRate ? '<span style="color: #e65100;">✓ พร้อมส่ง (อัตราปรับ)</span>' : '<span style="color: #28a745;">✓ พร้อมส่ง</span>')
                return `
                  <tr style="border-bottom: 1px solid #dee2e6; background-color: ${rowColor};">
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">${index + 1}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6; font-weight: bold; color: ${isBlocked ? '#e74c3c' : (isAdjustedRate ? '#e65100' : '#28a745')}; ${isAdjustedRate ? 'background-color: #ffecb3;' : ''}">${number || '-'}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">${originalItem?.betTypeLabel || originalItem?.betType || '-'}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6;">${(isBlocked ? 0 : (validatedItem.amount ?? originalItem?.amount ?? 0)).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 8px; text-align: right; border: 1px solid #dee2e6; color: ${isBlocked ? '#e74c3c' : (isAdjustedRate ? '#e65100' : '#28a745')}; font-weight: bold; ${isAdjustedRate ? 'background-color: #ffecb3;' : ''}">${payout.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6;">${(isBlocked ? 0 : (validatedItem.discount ?? 0)).toFixed(0)}</td>
                    <td style="padding: 8px; text-align: center; border: 1px solid #dee2e6; font-size: 12px;">${statusText}</td>
                  </tr>
                `
              }).join('')}
            </tbody>
          </table>
        </div>
        ${hasAnyAdjustedRate ? '<p style="margin: 8px 0 0 0; font-size: 12px; color: #e65100;"><span style="display: inline-block; width: 12px; height: 12px; background-color: #ffecb3; border: 1px solid #ffb74d; vertical-align: middle; margin-right: 4px;"></span> แถบสี = เลขที่ปรับอัตราจ่าย</p>' : ''}
      `

      const validItems = Number(summary?.validItems || 0)
      const invalidItems = Number(summary?.invalidItems || 0)
      const hasSendableItems = validItems > 0
      const warningMessage = invalidItems > 0
        ? `<p style="color: #e74c3c; font-size: 14px; text-align: center; margin: 10px 0; background-color: #ffebee; padding: 10px; border-radius: 5px;"><i class="fas fa-exclamation-triangle"></i> มีรายการไม่ผ่านเงื่อนไข ${invalidItems} รายการ กรุณาตรวจสอบก่อนยืนยัน</p>`
        : ''

      const result = await Swal.fire({
        title: `รายการทั้งหมด : ${betItems.length} รายการ`,
        html: `
          <div style="text-align: left; padding: 10px;">
            <div style="background-color: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 15px;">
              <p style="margin: 5px 0;"><strong>🎯 หวย:</strong> ${lotteryCode}</p>
              <p style="margin: 5px 0;"><strong>🔄 รอบวันที่:</strong> ${roundDisplay}</p>
              <p style="margin: 5px 0;"><strong>💰 รวมแทง:</strong> <span style="color: #e74c3c; font-weight: bold;">${totalBetAmountCalc.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</span></p>
              <p style="margin: 5px 0;"><strong>💸 รวมส่วนลด:</strong> ${totalDiscount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</p>
              <p style="margin: 5px 0;"><strong>💵 รวมเป็นเงิน:</strong> <span style="color: #28a745; font-weight: bold;">${(totalBetAmountCalc - totalDiscount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท</span></p>
              <p style="margin: 5px 0;"><strong>📝 จำนวน:</strong> ${betItems.length} รายการ (พร้อมส่ง: ${validItems}, ไม่ผ่านเงื่อนไข: ${invalidItems})</p>
            </div>
            <div style="margin: 15px 0;"><p style="font-weight: bold; margin-bottom: 10px; color: #333;">📋 รายละเอียดเลขที่แทง:</p>${betItemsTable}</div>
            ${warningMessage}
            <hr style="margin: 15px 0; border-color: #dee2e6;">
            <p style="color: #e74c3c; font-size: 14px; text-align: center; margin: 10px 0;"><i class="fas fa-warning"></i> ${hasSendableItems ? 'กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนยืนยัน' : 'ไม่มีรายการที่สามารถส่งเดิมพันได้ กรุณากลับไปแก้ไข'}</p>
          </div>
        `,
        icon: invalidItems > 0 ? 'warning' : 'question',
        showCancelButton: true,
        confirmButtonColor: '#28a745',
        cancelButtonColor: '#dc3545',
        confirmButtonText: hasSendableItems ? '<i class="fas fa-check"></i> ยืนยันการส่ง' : '<i class="fas fa-ban"></i> ไม่มีรายการส่งได้',
        cancelButtonText: '<i class="fas fa-arrow-left"></i> กลับไปแก้ไข',
        reverseButtons: true,
        width: '800px',
        focusConfirm: hasSendableItems,
        focusCancel: !hasSendableItems,
        didOpen: () => {
          if (!hasSendableItems) {
            const confirmButton = Swal.getConfirmButton()
            if (confirmButton) confirmButton.disabled = true
          }
        },
      })
      if (result.isConfirmed && submitBetItems) await submitBetItems()
    } catch (err: unknown) {
      Swal.close()
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'เกิดข้อผิดพลาดในการตรวจสอบเลข'
      toast(formatBlockedReason(msg), { icon: 'warning' })
    }
  }, [betItems, round, selectedPackage, submitBetItems, totalBet, walletBalance, betTypeToPrizeTypeCode, prizeTypeCodeForApi, formatRoundDisplay, formatBlockedReason, tenantId, baseUrl, isSubmittingBet])

  /** ล้างรายการทั้งหมด — แสดง SweetAlert ยืนยันก่อน (ตรง user-classic handleClearAllBetItems) */
  const handleClearAllBetItems = useCallback(() => {
    if (!clearAllBetItems) return
    if (betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ล้าง', { icon: 'info' })
      return
    }
    const lotteryCode = round?.huayName || round?.huayTypeName || 'ไม่ระบุ'
    const roundDisplay = formatRoundDisplay(round)
    Swal.fire({
      title: 'ยืนยันการล้างรายการทั้งหมด',
      html: `
        <div style="text-align: center;">
          <p><i class="fas fa-exclamation-triangle" style="color: #f39c12; font-size: 48px;"></i></p>
          <p><strong>คุณต้องการล้างรายการเดิมพันทั้งหมดใช่หรือไม่?</strong></p>
          <p style="color: #666;">หวย: <strong>${lotteryCode}</strong></p>
          <p style="color: #666;">${roundDisplay}</p>
          <p style="color: #666;">จำนวนรายการที่จะลบ: <strong>${betItems.length} รายการ</strong></p>
          <p style="color: #e74c3c; font-size: 14px;">
            <i class="fas fa-warning"></i> การดำเนินการนี้ไม่สามารถย้อนกลับได้
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: '<i class="fas fa-trash"></i> ล้างทั้งหมด',
      cancelButtonText: '<i class="fas fa-times"></i> ยกเลิก',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        clearAllBetItems()
        setDuplicateHighlight({ enabled: false, sameAmountItems: [], differentAmountItems: [] })
        toast(`ล้างรายการเดิมพันทั้งหมด ${betItems.length} รายการเรียบร้อยแล้ว`, { icon: 'success' })
      }
    })
  }, [betItems, clearAllBetItems, round, formatRoundDisplay])

  const payoutRatesData = useMemo(
    () => buildPayoutRatesTableData(selectedPackage?.prizeConfigs),
    [selectedPackage]
  )

  // จัดกลุ่มรายการเดิมพันตามประเภท (เดียวกับ user-classic)
  const groupedBetItems = useMemo(() => {
    const groups: Record<string, HuayBetItem[]> = {}
    betItems.forEach((item) => {
      if (!groups[item.betType]) groups[item.betType] = []
      groups[item.betType].push(item)
    })
    return groups
  }, [betItems])

  // ลำดับการแสดงประเภท (ตรงกับ user-classic LotteryBetSideRight)
  const betTypeDisplayOrder: Record<string, number> = {
    top2: 1,
    down2: 2,
    tod2: 3,
    tod2reverse: 4,
    top3: 5,
    down3: 6,
    tod3: 7,
    tod3reverse: 8,
    top1: 9,
    down1: 10,
    top4: 11,
    tod4: 12,
    tod5: 13,
    puk1: 14,
    puk10: 15,
    puk100: 16,
    '4 ตัวบน': 11,
    '4 ตัวโต๊ด': 12,
    '5 ตัวโต๊ด': 13,
    'ปักหลักหน่วย': 14,
    'ปักหลักสิบ': 15,
    'ปักหลักร้อย': 16,
    top3pick1group: 17,
    top5fourgroup: 18,
  }

  const getSortedBetTypes = (grouped: Record<string, HuayBetItem[]>) =>
    Object.entries(grouped).sort(([a], [b]) => (betTypeDisplayOrder[a] ?? 999) - (betTypeDisplayOrder[b] ?? 999))

  const clearDuplicateHighlight = useCallback(() => {
    setDuplicateHighlight({ enabled: false, sameAmountItems: [], differentAmountItems: [] })
  }, [])

  const checkDuplicateItems = useCallback(() => {
    if (betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ตรวจสอบ', { icon: 'info' })
      return
    }
    const groupedItems: Record<string, Record<string, HuayBetItem[]>> = {}
    const sameAmountItems: string[] = []
    const differentAmountItems: string[] = []
    betItems.forEach((item) => {
      if (!groupedItems[item.betType]) groupedItems[item.betType] = {}
      if (!groupedItems[item.betType][item.number]) groupedItems[item.betType][item.number] = []
      groupedItems[item.betType][item.number].push(item)
    })
    Object.values(groupedItems).forEach((numberGroups) => {
      Object.values(numberGroups).forEach((items) => {
        if (items.length > 1) {
          const amounts = [...new Set(items.map((i) => i.amount))]
          if (amounts.length === 1) items.forEach((i) => sameAmountItems.push(i.id))
          else items.forEach((i) => differentAmountItems.push(i.id))
        }
      })
    })
    const total = sameAmountItems.length + differentAmountItems.length
    if (total === 0) {
      toast('ไม่พบเลขซ้ำในทุกกลุ่ม')
      return
    }
    setDuplicateHighlight({ enabled: true, sameAmountItems, differentAmountItems })
    toast(`พบเลขซ้ำ ${total} รายการ (เงินเท่ากัน: ${sameAmountItems.length} | เงินไม่เท่ากัน: ${differentAmountItems.length})`, { icon: 'warning' })
  }, [betItems])

  const removeDuplicateItems = useCallback(() => {
    if (!removeBetItem || betItems.length === 0) {
      toast('ไม่มีรายการเดิมพันให้ตัด', { icon: 'info' })
      return
    }
    const groupedItems: Record<string, Record<string, HuayBetItem[]>> = {}
    const itemsToRemove: HuayBetItem[] = []
    betItems.forEach((item) => {
      if (!groupedItems[item.betType]) groupedItems[item.betType] = {}
      if (!groupedItems[item.betType][item.number]) groupedItems[item.betType][item.number] = []
      groupedItems[item.betType][item.number].push(item)
    })
    Object.values(groupedItems).forEach((numberGroups) => {
      Object.values(numberGroups).forEach((items) => {
        if (items.length > 1) items.slice(1).forEach((i) => itemsToRemove.push(i))
      })
    })
    if (itemsToRemove.length === 0) {
      toast('ไม่พบเลขซ้ำในทุกกลุ่มให้ตัด', { icon: 'info' })
      return
    }
    Swal.fire({
      title: 'ยืนยันการตัดเลขซ้ำ',
      html: `จะตัด <strong>${itemsToRemove.length}</strong> รายการ (เก็บไว้ 1 รายการต่อเลขในแต่ละกลุ่ม)`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'ตัดเลขซ้ำ',
      cancelButtonText: 'ยกเลิก',
    }).then((res) => {
      if (res.isConfirmed) {
        itemsToRemove.forEach((i) => removeBetItem(i.id))
        setDuplicateHighlight({ enabled: false, sameAmountItems: [], differentAmountItems: [] })
        toast(`ตัดเลขซ้ำ ${itemsToRemove.length} รายการแล้ว`)
      }
    })
  }, [betItems, removeBetItem])

  const handleAmountChange = useCallback(
    (itemId: string, value: string) => {
      const n = Math.max(1, parseInt(value, 10) || 1)
      updateBetAmount?.(itemId, n)
    },
    [updateBetAmount]
  )

  /** ใส่ราคาเท่ากันหมด — อัปเดตทุกรายการในโพยเป็นจำนวนที่กำหนด (ตรง user-classic handleBulkAmountUpdate) */
  const [bulkAmount, setBulkAmount] = useState('')
  const [groupBulkAmounts, setGroupBulkAmounts] = useState<Record<string, string>>({})
  const handleBulkAmountUpdate = useCallback(
    (amountValue: string | number) => {
      if (!updateBetAmount) return
      const amountNum = typeof amountValue === 'string' ? parseInt(amountValue, 10) : amountValue
      if (amountNum > 0 && betItems.length > 0) {
        betItems.forEach((item) => {
          updateBetAmount(item.id, amountNum)
        })
        setBulkAmount('')
      }
    },
    [updateBetAmount, betItems]
  )

  const handleGroupedBulkAmountUpdate = useCallback(
    (betType: string, amountValue: string | number) => {
      if (!updateBetAmount) return
      const amountNum = typeof amountValue === 'string' ? parseInt(amountValue, 10) : amountValue
      if (amountNum > 0) {
        betItems
          .filter((item) => item.betType === betType)
          .forEach((item) => {
            updateBetAmount(item.id, amountNum)
          })
        setGroupBulkAmounts((prev) => ({ ...prev, [betType]: '' }))
      }
    },
    [updateBetAmount, betItems]
  )

  const renderGroupAmountControls = useCallback(
    (betType: string, items: HuayBetItem[]) => {
      const groupedAmount = groupBulkAmounts[betType] || ''
      return (
        <div className="bet-slip-group-toolbar">
          <div
            className="bet-slip-group-amount box_row flex-nowrap overflow-hidden justify-content-between font_rg bdc_primary bg_primary border_5"
          >
            <input
              name={`amount-${betType}`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={5}
              placeholder="จำนวน"
              value={groupedAmount}
              onChange={(e) => setGroupBulkAmounts((prev) => ({ ...prev, [betType]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleGroupedBulkAmountUpdate(betType, groupedAmount)
                }
              }}
              className="bet-slip-group-amount-input border-0 text-center bg_secondary w-100"
              pattern="[0-9]*"
            />
            <button
              type="button"
              className="bet-slip-group-amount-apply border-0 bg-transparent px-2 c_tertiary"
              onClick={() => handleGroupedBulkAmountUpdate(betType, groupedAmount)}
              disabled={!groupedAmount.trim() || items.length === 0}
              aria-label={`ใส่ราคาเท่ากันสำหรับ ${items[0]?.betTypeLabel ?? betType}`}
            >
              <i className="fas fa-plus-circle" />
            </button>
          </div>

          <div className="bet-slip-group-pills nav nav-pills nav_pills_price justify-content-end">
            {PRICE_OPTIONS_SAME.map((p) => (
              <button
                key={`${betType}-${p}`}
                type="button"
                className="bet-slip-group-pill nav-link"
                onClick={() => handleGroupedBulkAmountUpdate(betType, p)}
                disabled={items.length === 0}
              >
                {p}฿
              </button>
            ))}
          </div>
        </div>
      )
    },
    [groupBulkAmounts, handleGroupedBulkAmountUpdate]
  )

  /** ตารางโพยตามประเภท (เรียงตาม betTypeDisplayOrder, กลุ่มละตาราง — ตรง user-classic) */
  const renderSlipTablesByGroup = () => {
    const hasItems = Object.keys(groupedBetItems).length > 0
    if (!hasItems) {
      return (
        <div className="light_sd border_5 mb-2 bet-slip-table-wrap">
          <div className="table-responsive">
          <table className="table bg_secondary table-striped table-huay-result text-center border_5 overflow-hidden mb-0">
            <thead className="bg_gd_primary">
              <tr>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '8%' }}>##</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '20%' }}>
                  <div className="bg_op_secondary bdc_primary border_5 c_tertiary py-1">ประเภทการเล่น</div>
                </th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '20%' }}>แทง (฿)</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '15%' }}>คูณ</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '20%' }}>ชนะ</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '12%' }}>ลบ</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={6} className="text-center p-4">
                  <p className="mb-2 c_secondary">
                    <i className="fas fa-info-circle me-2" />
                    ยังไม่มีรายการเดิมพัน
                  </p>
                  <small className="c_tertiary">กรุณาเลือกประเภทการเล่นและระบุตัวเลขในฝั่งซ้าย</small>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )
    }
    return getSortedBetTypes(groupedBetItems).map(([betType, items]) => {
      const betTypeLabel = items[0]?.betTypeLabel ?? betType
      return (
        <div key={betType} className="mb-3">
          <div className="bet-slip-group-header d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-2 mb-2">
            <h5 className="bet-slip-group-title c_tertiary font_md mb-0">
              {betTypeLabel}
            </h5>
            {renderGroupAmountControls(betType, items)}
          </div>
          <div className="light_sd border_5 mb-2 bet-slip-table-wrap">
            <div className="table-responsive">
            <table className="table bg_secondary table-striped table-huay-result text-center border_5 overflow-hidden mb-0" style={{ tableLayout: 'fixed' }}>
              <thead className="bg_gd_primary">
                <tr>
                  <th scope="col" className="c_tertiary bg-transparent" style={{ width: '8%' }}>##</th>
                  <th scope="col" className="c_tertiary bg-transparent" style={{ width: '20%' }}>
                    <div className="bg_op_secondary bdc_primary border_5 c_tertiary py-1">ชนิด</div>
                  </th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '20%' }}>แทง (฿)</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '15%' }}>คูณ</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '20%' }}>ชนะ</th>
                <th scope="col" className="c_tertiary bg-transparent" style={{ width: '12%' }}>ลบ</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const validatedItem = inlineValidationByItemId[item.id]
                const hasFreshValidation = hasFreshInlineValidation(item, validatedItem)
                const isPendingValidation = !hasFreshValidation
                const isHardBlockedItem = hasFreshValidation && isHardBlockedValidation(validatedItem)
                const validationNotice = hasFreshValidation ? getInlineValidationNotice(validatedItem) : null
                const isSameAmount = duplicateHighlight.enabled && duplicateHighlight.sameAmountItems.includes(item.id)
                const isDiffAmount = duplicateHighlight.enabled && duplicateHighlight.differentAmountItems.includes(item.id)
                const rowBg = validationNotice
                  ? '#fff4f4'
                  : isPendingValidation
                    ? '#fff8e1'
                  : isSameAmount
                    ? '#ffebee'
                    : isDiffAmount
                      ? '#fff3e0'
                      : undefined
                return (
                  <React.Fragment key={item.id}>
                    <tr style={rowBg ? { backgroundColor: rowBg } : undefined}>
                      <th scope="row" className="c_secondary">{index + 1}.</th>
                      <td>
                        <div
                          className="bg_gd_secondary border_5 c_tertiary font_md py-1 px-2"
                          style={{
                            color: '#ffffff',
                            fontWeight: 600,
                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.95)',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {item.number}
                        </div>
                      </td>
                      <td>
                        <div className="bg_secondary border_5 c_secondary font_rg py-1 px-1">
                          <input
                            type="number"
                            value={isHardBlockedItem ? 0 : item.amount}
                            onChange={(e) => handleAmountChange(item.id, e.target.value)}
                            min={1}
                            maxLength={4}
                            autoComplete="off"
                            className="border-0 w-100 text-center bg-transparent c_secondary"
                            disabled={isHardBlockedItem}
                          />
                        </div>
                      </td>
                      <td className="c_secondary">
                        {hasFreshValidation && validatedItem?.isValid
                          ? Number(validatedItem.payoutRate ?? item.multiplier).toLocaleString()
                          : '-'}
                      </td>
                      <td>
                        <div className="bg_secondary border_5 c_primary py-1">
                          {isPendingValidation ? 'รอตรวจสอบ' : (validatedItem?.potentialPayout ?? 0).toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="bg_primary border-0 bdc_primary border_5 py-1 px-2 w-100 hh_hover"
                          onClick={() => removeBetItem?.(item.id)}
                        >
                          <i className="fas fa-trash-alt c_tertiary" />
                        </button>
                      </td>
                    </tr>
                    {validationNotice && (
                      <tr style={{ backgroundColor: '#fff6f6' }}>
                        <td colSpan={6} className="text-start px-2 py-2">
                          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                            <div>
                              <div className="fw-bold" style={{ color: '#c0392b' }}>{validationNotice.title}</div>
                              {validationNotice.detail && (
                                <small className="d-block text-muted mt-1">{validationNotice.detail}</small>
                              )}
                            </div>
                            {validationNotice.suggestedAmount > 0 && updateBetAmount && (
                              <button
                                type="button"
                                className="btn btn-sm btn-warning align-self-start align-self-md-center"
                                onClick={() => updateBetAmount(item.id, validationNotice.suggestedAmount)}
                              >
                                ปรับเป็น {formatAmountDisplay(validationNotice.suggestedAmount)} บาท
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
            </div>
          </div>
        </div>
      )
    })
  }

  return (
    <div className="col-12 col-lg-6 px-1">
      <div className="d-grid gap-2">
        {/* 1964-1985 */}
        <div className="h_box p-0">
          <div className="d-flex align-items-center c_tertiary">
            <h5 className="">
              <i className="fas fa-dollar-sign" />
            </h5>
            <h6 className="font_md ms-2">ใส่ราคา</h6>
          </div>
          <div className="ms-auto box_row gap-2">
            <div className="bg_secondary border_5 px-2 py-1 c_primary box_row justify-content-center">
              <h6>แทง {betItems.length} รายการ</h6>
            </div>
            {/* <div className="bg_gd_primary border_5 px-2 py-1 c_tertiary bdc_primary box_row justify-content-center">
              <h6>{countdown}</h6>
            </div> */}
          </div>
        </div>

        {/* 1988-2016: ส่งโพย — กรณีรวมยอดแทง > ยอดเครดิต แสดงจำนวนเงินไม่เพียงพอและกดไม่ได้ */}
        {/* <div className="row mx__1" style={rowStyle}>
          <div className="col-6 px-1">
            <a
              href=""
              className={`link_pmr ins_sd box_row justify-content-center font_rg border_10 ${(totalBet > walletBalance || isSubmittingBet) ? 'bg_quaternary' : 'bg_gd_quaternary'}`}
              onClick={(e) => {
                e.preventDefault()
                if (betItems.length === 0 || totalBet > walletBalance || isSubmittingBet) return
                handleSubmitBetConfirmation()
              }}
              style={betItems.length === 0 || totalBet > walletBalance || isSubmittingBet ? { pointerEvents: 'none' } : undefined}
            >
              <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                <i className="fas fa-paper-plane me-1" />
                <span className="">{isSubmittingBet ? 'กำลังส่งโพย...' : totalBet > walletBalance ? 'จำนวนเงินไม่เพียงพอ' : 'ส่งโพย'}</span>
              </div>
            </a>
          </div>
          <div className="col-6 px-1">
            <a
              href=""
              className="link_pmr ins_sd box_row justify-content-center font_rg bdc_primary bg_secondary border_10"
              onClick={(e) => { e.preventDefault(); handleClearAllBetItems(); }}
            >
              <div className="d-flex align-items-center c_primary font_md">
                <i className="fas fa-trash-alt me-1" />
                <span className="">ล้างรายการ</span>
              </div>
            </a>
          </div>
        </div> */}

        {/* ตารางโพยตามประเภท — แต่ละประเภทมี h5 (2 ตัวบน, 2 ตัวล่าง, ...) แล้วตามด้วยตาราง */}
        {betItems.length > 0 && (
          <div
            className="border_5 px-3 py-2"
            style={{
              backgroundColor: isInlineValidating ? '#fff8e1' : '#eef7ff',
              border: `1px solid ${isInlineValidating ? '#f4c542' : '#b8daff'}`,
            }}
          >
            <small className="d-block" style={{ color: isInlineValidating ? '#8a6d1d' : '#2c5aa0' }}>
              {isInlineValidating
                ? 'กำลังตรวจสอบยอดซื้อและวงเงินของแต่ละรายการ...'
                : 'ระบบจะแจ้งเตือนอัตโนมัติเมื่อยอดซื้อเกินวงเงิน และสามารถกดปรับยอดได้ทันที'}
            </small>
          </div>
        )}
        {renderSlipTablesByGroup()}

        {/* ดูเลขซ้ำ + ตัดเลขซ้ำ */}
        <div className="row mx__1" style={rowStyle}>
          <div className="col-6 px-1">
            <button
              type="button"
              className={`link_pmr ins_sd box_row justify-content-center font_rg border_10 w-100 ${duplicateHighlight.enabled ? 'bdc_primary bg_gd_secondary' : 'bdc_primary bg_gd_primary'}`}
              onClick={duplicateHighlight.enabled ? clearDuplicateHighlight : checkDuplicateItems}
              disabled={betItems.length === 0}
            >
              <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                <i className={`fas ${duplicateHighlight.enabled ? 'fa-eye-slash' : 'fa-eye'} me-2`} />
                <span>{duplicateHighlight.enabled ? 'ปิดการแสดงซ้ำ' : 'ดูเลขซ้ำ'}</span>
              </div>
            </button>
          </div>
          <div className="col-6 px-1">
            <button
              type="button"
              className="link_pmr ins_sd box_row justify-content-center font_rg bdc_primary bg_gd_secondary border_10 w-100"
              onClick={removeDuplicateItems}
              disabled={betItems.length === 0}
            >
              <div className="d-flex align-items-center c_tertiary font_md">
                <i className="fas fa-cut me-2" />
                <span>ตัดเลขซ้ำ</span>
              </div>
            </button>
          </div>
        </div>

        {/* 2592-2594 */}
        <h5 className="text-center c_tertiary font_md">ใส่ราคาเท่ากันหมด</h5>

        {/* 2596-2693: input + pills 5-500 — ใส่ราคาเท่ากันหมด (ตรง user-classic) */}
        <div className="row mx__1" style={rowStyle}>
          <div className="col-12 col-md-4 px-1">
            <div className="box_row flex-nowrap overflow-hidden justify-content-between font_rg bdc_primary bg_primary border_5 h-100">
              <input
                name="amount"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={5}
                placeholder="จำนวน"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                className="border-0 text-center bg_secondary w-100"
                style={{ height: 30 }}
                pattern="[0-9]*"
              />
              <button
                type="button"
                className="border-0 bg-transparent px-3 c_tertiary"
                onClick={() => handleBulkAmountUpdate(bulkAmount)}
                disabled={!bulkAmount.trim() || betItems.length === 0}
                aria-label="ใส่ราคาเท่ากันหมด"
              >
                <i className="fas fa-plus-circle" />
              </button>
            </div>
          </div>
          <div className="col-12 col-md-8 px-1">
            <div className="nav nav-pills nav_pills_price">
              {PRICE_OPTIONS_SAME.map((p) => (
                <button
                  key={p}
                  type="button"
                  className="nav-link"
                  onClick={() => handleBulkAmountUpdate(p)}
                  disabled={betItems.length === 0}
                >
                  {p}฿
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 2696-2726 */}
        <div className="row mx__1" style={rowStyle}>
          <div className="col-6 px-1">
            <div className="box_row justify-content-center font_rg bdc_primary bg_secondary border_5">
              <div className="d-grid gap-1 align-items-center text-center ft_sd c_tertiary py-2">
                <small className="bg_gd_secondary px-1 border_5">ยอดเครดิตคงเหลือ</small>
                <h5 className="c_secondary font_rg">{isLoadingWallet ? creditDisplayText : `฿ ${creditDisplayText}`}</h5>
              </div>
            </div>
          </div>
          <div className="col-6 px-1">
            <div className="box_row justify-content-center font_rg bdc_primary bg_op_secondary border_5">
              <div className="d-grid gap-1 align-items-center text-center ft_sd c_tertiary py-2">
                <small className="bg_gd_primary px-1 border_5">รวมยอดแทง</small>
                <h5 className="font_rg">฿ {totalBet.toFixed(2)}</h5>
              </div>
            </div>
          </div>
        </div>

        {/* 2729-2758: ส่งโพย — กรณีรวมยอดแทง > ยอดเครดิต แสดงจำนวนเงินไม่เพียงพอและกดไม่ได้ */}
        <div className="row mx__1" style={rowStyle}>
          <div className="col-6 px-1">
            <a
              href="#sendModal"
              className={`link_pmr ins_sd box_row justify-content-center font_rg border_10 ${(totalBet > walletBalance || isSubmittingBet) ? 'bg_quaternary' : 'bg_gd_quaternary'}`}
              onClick={(e) => {
                e.preventDefault()
                if (betItems.length === 0 || totalBet > walletBalance || isSubmittingBet) return
                handleSubmitBetConfirmation()
              }}
              style={betItems.length === 0 || totalBet > walletBalance || isSubmittingBet ? { pointerEvents: 'none' } : undefined}
            >
              <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                <i className="fas fa-paper-plane me-1" />
                <span className="">{isSubmittingBet ? 'กำลังส่งโพย...' : totalBet > walletBalance ? 'จำนวนเงินไม่เพียงพอ' : 'ส่งโพย'}</span>
              </div>
            </a>
          </div>
          <div className="col-6 px-1">
            <a
              href=""
              className="link_pmr ins_sd box_row justify-content-center font_rg bdc_primary bg_secondary border_10"
              onClick={(e) => { e.preventDefault(); handleClearAllBetItems(); }}
            >
              <div className="d-flex align-items-center c_primary font_md">
                <i className="fas fa-trash-alt me-1" />
                <span className="">ล้างรายการ</span>
              </div>
            </a>
          </div>
        </div>

        {/* ยิงเลขยี่กี่ — UI ตรง huaybet.html 3061-3361 */}
        {isYeekee && (
          <>
            <h3 className="text-center c_tertiary font_md">
              <i className="fas fa-crosshairs me-2" />ยิงเลขยี่กี่
            </h3>
            <h1 className="text-center c_tertiary bdc_primary bg_op_secondary border_5 px-2 py-1 font_md">
              {yeekeeCountdown}
            </h1>

            <div className="d-grid gap-3">
              <h3 className="text-center c_tertiary font_md">ระบุตัวเลข</h3>
              <div className="huay_fields justify-content-center">
                {Array.from({ length: yeekeeNumberDigits }, (_, i) => i).map((i) => (
                  <div key={i} className="huay_fields_item">
                    <input
                      ref={(el) => {
                        yeekeeInputRefs.current[i] = el
                      }}
                      type="text"
                      maxLength={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="text-center border-0 bg-transparent c_secondary font_rg"
                      value={predictionNumber[i] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 1)
                        setPredictionNumber((prev) => {
                          const arr = prev.split('')
                          arr[i] = v
                          return arr.join('').slice(0, yeekeeNumberDigits)
                        })
                        if (v && i < yeekeeNumberDigits - 1) setTimeout(() => yeekeeInputRefs.current[i + 1]?.focus(), 0)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !(predictionNumber[i] ?? '') && i > 0) {
                          yeekeeInputRefs.current[i - 1]?.focus()
                        }
                      }}
                    />
                  </div>
                ))}
              </div>

              <div className="row" style={{ rowGap: '4px', margin: '0 -2px' }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <div key={n} className="col-4" style={{ padding: '0 2px' }}>
                    <div
                      className="hh_hover bg_secondary bdc_primary ins_sd border_5 py-1 h_num"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (predictionNumber.length < yeekeeNumberDigits) setPredictionNumber((prev) => prev + String(n))
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && predictionNumber.length < yeekeeNumberDigits && setPredictionNumber((prev) => prev + String(n))}
                    >
                      <h3 className="c_secondary font_rg text-center">{n}</h3>
                    </div>
                  </div>
                ))}
                <div className="col-4" style={{ padding: '0 2px' }} />
                <div className="col-4" style={{ padding: '0 2px' }}>
                  <div
                    className="hh_hover bg_secondary bdc_primary ins_sd border_5 py-1 h_num"
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (predictionNumber.length < yeekeeNumberDigits) setPredictionNumber((prev) => prev + '0') }}
                    onKeyDown={(e) => e.key === 'Enter' && predictionNumber.length < yeekeeNumberDigits && setPredictionNumber((prev) => prev + '0')}
                  >
                    <h3 className="c_secondary font_rg text-center">0</h3>
                  </div>
                </div>
                <div className="col-4" style={{ padding: '0 2px' }}>
                  <button
                    type="button"
                    className="hh_hover bg_gd_secondary bdc_primary ins_sd border_5 py-1 w-100 h_num"
                    onClick={() => setPredictionNumber((prev) => prev.slice(0, -1))}
                  >
                    <h4 className="c_tertiary font_rg text-center"><i className="fas fa-backspace" /></h4>
                  </button>
                </div>
                <div className="col-6" style={{ padding: '0 2px' }}>
                  <button
                    type="button"
                    className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 ins_sd w-100 border-0"
                    onClick={handleRandomPrediction}
                  >
                    <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                      <i className="fas fa-random me-2" />
                      <span>สุ่มเลข</span>
                    </div>
                  </button>
                </div>
                <div className="col-6" style={{ padding: '0 2px' }}>
                  <button
                    type="button"
                    className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd w-100 border-0"
                    onClick={handleSubmitPrediction}
                    disabled={loadingShots}
                  >
                    <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                      <i className="fas fa-plus-circle me-2" />
                      <span>เพิ่มเลข</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            <h3 className="text-center c_tertiary font_md">ผลรวมเลข</h3>
            <h1 className="text-center c_tertiary bdc_primary bg_op_secondary border_5 px-2 py-1 font_md">
              {(() => {
                const yeekeeResult = (round as { yeekee?: { result?: { sumTotal?: number } } })?.yeekee?.result
                if (yeekeeResult?.sumTotal != null) return String(yeekeeResult.sumTotal)
                const sum = yeekeeShots.reduce((acc, s) => acc + Number(s.number || 0), 0)
                return sum > 0 ? String(sum) : '0'
              })()}
            </h1>
            {((round as { yeekee?: { result?: { sumTotal?: number } } })?.yeekee?.result?.sumTotal == null) && yeekeeShots.length > 0 && yeekeeShots.length < 25 && (
              <p className="text-center mb-0 small c_secondary">ส่งแล้ว {yeekeeShots.length} ชุด (รางวัลต้องมี 25 ชุดขึ้นไป)</p>
            )}

            <div className="light_sd border_5">
              <div className="yeekee-shot-table-scroll">
                <table className="table bg_secondary table-striped table-huay-result text-center border_5 overflow-hidden mb-0">
                <thead className="bg_gd_primary">
                  <tr>
                    <th scope="col" className="c_tertiary bg-transparent" style={{ width: '15%' }}>ลำดับ</th>
                    <th scope="col" className="c_tertiary bg-transparent" style={{ width: '25%' }}>
                      <div className="bg_secondary bdc_primary border_5 c_primary font_md py-1">เลข</div>
                    </th>
                    <th scope="col" className="c_tertiary bg-transparent" style={{ width: '30%' }}>สมาชิก</th>
                    <th scope="col" className="c_tertiary bg-transparent" style={{ width: '30%' }}>เวลา</th>
                  </tr>
                </thead>
                  <tbody>
                    {loadingShots ? (
                      <tr>
                        <td colSpan={4} className="text-center py-3 c_secondary">
                          <i className="fas fa-spinner fa-spin me-2" />กำลังโหลดข้อมูล...
                        </td>
                      </tr>
                    ) : yeekeeShots.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-3 c_secondary">ยังไม่มีผู้ร่วมส่งเลข</td>
                      </tr>
                    ) : (
                      (() => {
                        const maskMember = (raw: string): string => {
                          const s = String(raw).trim()
                          if (!s || s.length < 4) return s
                          if (/^\d+$/.test(s)) {
                            if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
                            return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
                          }
                          if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
                          return s.slice(0, 2) + 'xxxxx' + s.slice(-3)
                        }
                        const botDisplayNameFallback = (id: string) => {
                          const letters = 'abcdefghijklmnopqrstuvwxyz'
                          let n = 0
                          for (let i = 0; i < (id || '').length; i++) n = (n * 31 + (id.charCodeAt(i) || 0)) >>> 0
                          const r = () => { n = (n * 1103515245 + 12345) >>> 0; return n }
                          const p = () => letters[r() % 26]
                          return p() + p() + 'xxxxx' + p() + p() + String(r() % 10)
                        }
                        return yeekeeShots.map((shot, index) => {
                          const shotTime = new Date(shot.shotAt)
                          const timeStr = shotTime.toLocaleString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          const isBot = String(shot.shooterType) === 'bot'
                          const memberName = isBot
                            ? (shot.username?.includes('xxxxx') ? shot.username : botDisplayNameFallback(shot._id != null ? String(shot._id) : String(index)))
                            : (shot.username ? maskMember(shot.username) : shot.displayPhoneNumber ? maskMember(shot.displayPhoneNumber) : 'สมาชิก')
                          const isBonus5 = index === 4
                          const isBonus16 = index === 15
                          return (
                            <tr key={(shot._id ?? shot.number) + '-' + index}>
                              <th scope="row">
                                {isBonus5 ? (
                                  <div className="bg_gd_quaternary border_5 px-1 c_tertiary mx-auto" style={{ width: 'fit-content' }}>{index + 1}</div>
                                ) : isBonus16 ? (
                                  <div className="bg_gd_primary border_5 px-1 c_tertiary mx-auto" style={{ width: 'fit-content' }}>{index + 1}</div>
                                ) : (
                                  index + 1
                                )}
                              </th>
                              <td>
                                <div className="bg_primary border_5 c_tertiary font_md py-1">{shot.number}</div>
                              </td>
                              <td className="c_secondary">{memberName}</td>
                              <td>
                                <div className="bg_secondary border_5 c_primary font_md py-1">{timeStr}</div>
                              </td>
                            </tr>
                          )
                        })
                      })()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ตารางรางวัล — ดึงจาก selectedPackage (ตรง user-classic) */}
        <h3 className="text-center c_tertiary font_md">ราคาจ่าย</h3>
        <div className="light_sd border_5 huay-bet-payout-panel">
          {payoutRatesData.length > 0 ? (
            <div className="table-responsive w-100">
              <table className="table table-huay-result huay-bet-payout-table text-center border_5 overflow-hidden mb-0">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: '15%' }}>ลำดับ</th>
                    <th scope="col" style={{ width: '35%' }}>ชนิด</th>
                    <th scope="col" style={{ width: '25%' }}>จ่าย(บ.)</th>
                    <th scope="col" style={{ width: '25%' }}>ลด(%)</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutRatesData.map((item, index) => (
                    <tr key={`${item.name}-${index}`}>
                      <td>{index + 1}.</td>
                      <td>
                        <span className="huay-bet-payout-table__type">{item.name}</span>
                      </td>
                      <td className="huay-bet-payout-table__amount">{item.payout.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{item.discount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center p-3">
              <p className="mb-0 c_secondary">
                <i className="fas fa-exclamation-triangle me-2" />
                ไม่พบข้อมูลรางวัลสำหรับหวยนี้
              </p>
              <small className="c_tertiary">
                กรุณาติดต่อเจ้าหน้าที่หากปัญหานี้ยังคงเกิดขึ้น
              </small>
            </div>
          )}

          {/* ปุ่มเปลี่ยนแปลงราคาแทง (ตรง user-classic) */}
          {payoutRatesData.length > 0 && (
            <div className="w-100 mt-2">
              <button
                type="button"
                className="w-100 border-0 bdc_primary bg_gd_quaternary c_tertiary font_rg py-2 border_5"
                style={{ fontSize: '14px' }}
                disabled={packageCount !== undefined && packageCount <= 1}
                title={packageCount !== undefined && packageCount <= 1 ? 'มีแพ็กเกจเดียว ไม่สามารถเปลี่ยนราคาได้' : undefined}
                onClick={() => {
                  if (!round) {
                    toast('ไม่พบข้อมูลรอบหวย กรุณาเลือกรอบใหม่', { icon: 'warning' })
                    navigate('/lobby-huay/listall')
                    return
                  }
                  navigate('/lobby-huay/lottery-terms', {
                    state: { round, fromBetPage: true },
                  })
                }}
              >
                <i className="fas fa-arrows-alt-v me-2" />
                เปลี่ยนแปลงราคาแทง
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ModernLobbyHuayBetSideRight
